import React, { useState } from "react";
import { ProductCardProps } from "./ProductCard.types";
import { ProductImage, StyledActionButton } from "./ProductCard.styles";
import { IMAGE_PREFIX } from "../../../utils/constants";
import FlexColumn from "../../../components/Layout/FlexColumn/FlexColumn";
import {
  Body5MediumTypography,
  Body6MediumTypography,
  Body6RegularTypography,
  Heading2SemiboldTypography,
} from "../../../primitives/Typography/typographyVariants";
import { Icon } from "../../../components/Icon/Icon";
import { colorPalette } from "../../../theme/colors";
import { BottomSheet } from "../../../components/BottomSheet/BottomSheet";
import RadioGroup from "../../../components/RadioGroup/RadioGroup";
import VerticalSpacer from "../../../primitives/Spacer/VerticalSpacer";
import Box from "../../../primitives/Box/Box";
import { Tags } from "../../../components/Tags/Tags";
import FlexRow from "../../../components/Layout/FlexRow/FlexRow";
import { Button } from "../../../components/Button/Button";

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  collabId,
  paymentType,
  productType,
  disabled = false,
  onAddToCart,
  onProductClick,
  showAddToCartButton = true,
  showSaleTag = true,
  dataCy = "product-card",
  idx,
}) => {
  const {
    name,
    description,
    brand = {},
    mrp = 1,
    price = 1,
    thumbnail_url,
    product_options = [],
  } = product;

  const [drawer, setDrawer] = useState(false);
  const [productOptionId, setProductOptionId] = useState(
    product_options[0]?.id
  );
  const get_sale_tag = (
    start_date: string,
    end_date: string,
    is_marquee: boolean = false
  ): string => {
    const current_date = new Date();
    if (start_date && end_date) {
      const start = new Date(start_date);
      const end = new Date(end_date);

      const daysUntilStart =
        (start.getTime() - current_date.getTime()) / (1000 * 60 * 60 * 24);

      if (current_date >= start && current_date <= end) {
        return is_marquee ? "SALE IS LIVE" : "SALE ONGOING";
      }
      if (daysUntilStart >= 0 && daysUntilStart < 14) {
        return is_marquee ? "SALE STARTING SOON" : "UPCOMING SALE";
      }

      return "";
    }
    return "";
  };

  const sanitize_price = (num: number): string => {
    if (!num) {
      return "0";
    }
    const number = Number.parseInt(num.toString());
    return number.toLocaleString();
  };
  const { brand_sale_offer } = brand;
  const { start_date, end_date } = brand_sale_offer || {};
  const sale_tag = get_sale_tag(start_date, end_date);
  const optionType = product_options[0]?.title || "";
  const productsOptions = product_options?.map((item: any) => ({
    label: item?.title,
    value: item?.id,
  }));
  const skipProductOptions =
    optionType === "Default" || optionType === "default";

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault(); // Add this
    e.stopPropagation(); // Keep this
    if (skipProductOptions) {
      handleAddToCartWithOption();
    } else {
      setDrawer(true);
    }
  };

  const handleProductOption = (data: any) => {
    setProductOptionId(data?.value);
  };

  const handleProductNavigation = () => {
    onProductClick?.(product);
  };

  const handleAddToCartWithOption = () => {
    onAddToCart?.(productOptionId, 1, true);
    setDrawer(false);
  };

  const discountPercentage =
    mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;

  return (
    <FlexColumn
      gap="small"
      alignItems="center"
      pb={8}
      borderRadius={8}
      backgroundColor={colorPalette.popWhite[400]}
      minWidth="155px"
    >
      <Box width="100%" onClick={handleProductNavigation} data-cy={dataCy}>
        <FlexColumn position="relative" alignItems="center">
          {showSaleTag && sale_tag && (
            <Box
              position="absolute"
              top={-10}
              justifyContent="center"
              alignItems="center"
            >
              <Tags
                type="category"
                backgroundColor={colorPalette.black[200]}
                label="upcoming sale"
                color={colorPalette.popWhite[500]}
              />
            </Box>
          )}
          <ProductImage
            src={thumbnail_url || `${IMAGE_PREFIX}/Wishlink-logo.png`}
            alt={`${name} product image`}
            data-cy={`${dataCy}-image`}
          />
          {showSaleTag && sale_tag && (
            <FlexRow
              gap={4}
              alignItems="center"
              justifyContent="center"
              py={2}
              pr={4}
              backgroundColor={colorPalette.warning[500]}
            >
              <Icon
                name="fa-solid fa-receipt"
                size="11"
                height="17"
                width="17"
                color={colorPalette.popBlack[200]}
              />
              <Body6RegularTypography>{sale_tag}</Body6RegularTypography>
            </FlexRow>
          )}
        </FlexColumn>
        <FlexColumn gap={2} px={8} alignItems="flex-start">
          <Body5MediumTypography
            color={colorPalette.popOrange[500]}
            lineClamp={1}
            overflow="hidden"
            textOverflow="ellipsis"
            textTransform="uppercase"
            maxWidth="150px"
            data-cy={`${dataCy}-name`}
          >
            {name}
          </Body5MediumTypography>
          <Body6RegularTypography
            overflow="hidden"
            textOverflow="ellipsis"
            lineClamp={1}
            maxWidth="150px"
            data-cy={`${dataCy}-description`}
          >
            {description}
          </Body6RegularTypography>
        </FlexColumn>
        <FlexRow gap={4} alignItems="flex-start" px={8}>
          {mrp > price && (
            <Body5MediumTypography
              textDecoration="line-through"
              color={colorPalette.popBlack[100]}
              data-cy={`${dataCy}-mrp`}
            >
              &#8377; {sanitize_price(mrp)}
            </Body5MediumTypography>
          )}
          <Body5MediumTypography
            color={colorPalette.popBlack[500]}
            data-cy={`${dataCy}-price`}
          >
            &#8377; {sanitize_price(price)}
          </Body5MediumTypography>
          {discountPercentage > 0 && (
            <Body6MediumTypography color={colorPalette.info[400]}>
              {`${discountPercentage}% OFF`}
            </Body6MediumTypography>
          )}
        </FlexRow>
      </Box>

      {showAddToCartButton && (
        <FlexRow px={8}>
          <StyledActionButton
            variant="primary"
            outlined={true}
            disabled={disabled}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleAddToCart(e);
            }}
            dataCy={`${dataCy}-add-to-cart`}
            leftIcon={
              <FlexColumn
                alignItems="center"
                justifyContent="center"
                p={4}
                height="28px"
                width="28px"
                color="inherit"
              >
                <Icon
                  name="fa-sharp fa-solid fa-cart-shopping"
                  size="14px"
                  height="20px"
                  width="20px"
                  color="inherit"
                />
              </FlexColumn>
            }
          >
            Add to Cart
          </StyledActionButton>
        </FlexRow>
      )}

      <BottomSheet
        open={drawer}
        handleClose={() => setDrawer(false)}
        dataCy="product-type-selection-drawer"
      >
        <FlexColumn pt="small" pb="small">
          <Heading2SemiboldTypography
            fontFamily="Lexend"
            color={colorPalette.popBlack[500]}
          >
            Select
          </Heading2SemiboldTypography>
          <VerticalSpacer size="large" />
          <RadioGroup
            options={productsOptions}
            value={productOptionId}
            onChange={handleProductOption}
            dataCyPrefix="size-option"
          />
          <VerticalSpacer size="large" />
          <Button
            variant="primary"
            onClick={handleAddToCartWithOption}
            dataCy="add-to-cart-confirm-button"
          >
            Add to cart
          </Button>
        </FlexColumn>
      </BottomSheet>
    </FlexColumn>
  );
};
